<template>
  <v-expansion-panels
    v-model="panel"
    multiple
  >
    <v-expansion-panel>
      <v-expansion-panel-header>Основные параметры</v-expansion-panel-header>
      <v-expansion-panel-content eager>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model.number="value.configuration.sync_interval_min"
              label="Интервал синхронизации, мин"
              :disabled="readonly"
              :error-messages="errors['skud.configuration.sync_interval_min']"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model.number="value.configuration.telemetry_interval_min"
              label="Интервал телеметрии, мин"
              :disabled="readonly"
              :error-messages="errors['skud.configuration.telemetry_interval_min']"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model.number="value.configuration.screensaver_interval"
              label="Интервал заставки, сек"
              :disabled="readonly"
              :error-messages="errors['skud.configuration.screensaver_interval']"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-switch
              v-model="value.configuration.sound_enabled"
              label="Звук"
              :disabled="readonly"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-text-field
              v-model.number="value.configuration.volume"
              label="Громкость (0–100)"
              :disabled="readonly"
              :error-messages="errors['skud.configuration.volume']"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-select
              v-model="value.configuration.reentry_block"
              :items="blockModeItems"
              label="Повторный вход"
              :disabled="readonly"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-select
              v-model="value.configuration.reexit_block"
              :items="blockModeItems"
              label="Повторный выход"
              :disabled="readonly"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-switch
              v-model="value.configuration.guest_enabled"
              label="Гостевой вход"
              :disabled="readonly"
            />
          </v-col>
          <v-col
            cols="12"
            md="4"
          >
            <v-switch
              v-model="value.configuration.qr_enabled"
              label="Вход по QR"
              :disabled="readonly"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel>
      <v-expansion-panel-header>Вход по PIN</v-expansion-panel-header>
      <v-expansion-panel-content eager>
        <div
          v-if="!value.pin_entrance_schedule.length"
          class="mb-3"
        >
          Правил запроса PIN нет
        </div>
        <v-card
          v-for="(rule, ri) in value.pin_entrance_schedule"
          :key="ri"
          outlined
          class="mb-4 pa-3"
        >
          <v-row align="center">
            <v-col
              cols="12"
              md="2"
            >
              <v-switch
                v-model="rule.enabled"
                label="Включено"
                :disabled="readonly"
              />
            </v-col>
            <v-col
              cols="12"
              md="3"
            >
              <v-text-field
                v-model="rule.pin"
                label="PIN"
                :type="pinVisible[ri] ? 'text' : 'password'"
                :append-icon="pinVisible[ri] ? 'mdi-eye' : 'mdi-eye-off'"
                autocomplete="off"
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].pin`]"
                @click:append="togglePin(ri)"
              />
            </v-col>
            <v-col
              v-if="!readonly"
              cols="12"
              md="2"
            >
              <v-btn
                icon
                @click="removeRule(ri)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <div
            v-if="errors[`skud.pin_entrance_schedule[${ri}]`]"
            class="error--text text-caption mb-2"
          >
            {{ errors[`skud.pin_entrance_schedule[${ri}]`].join('; ') }}
          </div>
          <div class="text-subtitle-1">
            Периоды по дням недели
          </div>
          <v-row
            v-for="(day, di) in rule.days"
            :key="di"
            align="center"
          >
            <v-col
              cols="12"
              md="4"
            >
              <v-select
                v-model="day.start_days"
                :items="dayItems"
                label="Дни недели"
                multiple
                chips
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].days[${di}].start_days`]"
              />
            </v-col>
            <v-col
              cols="6"
              md="3"
            >
              <v-text-field
                v-model="day.from"
                label="С"
                placeholder="HH:mm"
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].days[${di}].from`]"
              />
            </v-col>
            <v-col
              cols="6"
              md="3"
            >
              <v-text-field
                v-model="day.to"
                label="До"
                placeholder="HH:mm"
                :hint="toHint(day)"
                persistent-hint
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].days[${di}].to`]"
              />
            </v-col>
            <v-col
              v-if="!readonly"
              cols="12"
              md="1"
            >
              <v-btn
                icon
                @click="removeDay(ri, di)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn
            v-if="!readonly"
            small
            outlined
            class="mb-3"
            @click="addDay(ri)"
          >
            Добавить период
          </v-btn>
          <div class="text-subtitle-1">
            Исключения
          </div>
          <v-row
            v-for="(ex, ei) in rule.exceptions"
            :key="ei"
            align="center"
          >
            <v-col
              cols="12"
              md="4"
            >
              <v-text-field
                v-model="ex.date"
                label="Дата"
                type="date"
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].exceptions[${ei}].date`]"
              />
            </v-col>
            <v-col
              cols="6"
              md="3"
            >
              <v-text-field
                v-model="ex.from"
                label="С"
                placeholder="HH:mm"
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].exceptions[${ei}].from`]"
              />
            </v-col>
            <v-col
              cols="6"
              md="3"
            >
              <v-text-field
                v-model="ex.to"
                label="До"
                placeholder="HH:mm"
                :hint="toHint(ex)"
                persistent-hint
                :disabled="readonly"
                :error-messages="errors[`skud.pin_entrance_schedule[${ri}].exceptions[${ei}].to`]"
              />
            </v-col>
            <v-col
              v-if="!readonly"
              cols="12"
              md="1"
            >
              <v-btn
                icon
                @click="removeException(ri, ei)"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn
            v-if="!readonly"
            small
            outlined
            @click="addException(ri)"
          >
            Добавить исключение
          </v-btn>
        </v-card>
        <v-btn
          v-if="!readonly"
          color="primary"
          outlined
          :disabled="value.pin_entrance_schedule.length >= 32"
          @click="addRule()"
        >
          <span class="mx-3">Добавить правило</span>
        </v-btn>
      </v-expansion-panel-content>
    </v-expansion-panel>
    <v-expansion-panel>
      <v-expansion-panel-header>Перезагрузка</v-expansion-panel-header>
      <v-expansion-panel-content eager>
        <div
          v-if="!value.reboot_schedule.hour.length"
          class="mb-3"
        >
          Перезагрузки не запланированы
        </div>
        <v-row>
          <v-col
            cols="12"
            md="6"
          >
            <v-select
              v-model="value.reboot_schedule.hour"
              :items="hourItems"
              label="Часы перезагрузки"
              multiple
              chips
              :disabled="readonly"
            />
          </v-col>
          <v-col
            cols="12"
            md="6"
          >
            <v-text-field
              v-model.number="value.reboot_schedule.min"
              label="Минута (0–59)"
              :disabled="readonly"
              :error-messages="errors['skud.reboot_schedule.min']"
            />
          </v-col>
        </v-row>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
// Содержимое вкладки СКУД. Не обращается к API: модель приходит через v-model,
// серверные ошибки — через setServerErrors. Проверка пересечений — только на сервере.
import moment from 'moment'

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

export default {
  name: 'SkudConfigurationForm',
  props: {
    value: { type: Object, required: true },
    readonly: { type: Boolean, default: false }
  },
  data: () => ({
    panel: [0],
    errors: {},
    pinVisible: {},
    blockModeItems: [
      { value: 0, text: 'Не контролировать' },
      { value: 1, text: 'Уведомлять' },
      { value: 2, text: 'Блокировать' }
    ],
    dayItems: [
      { value: 'MONDAY', text: 'Понедельник' },
      { value: 'TUESDAY', text: 'Вторник' },
      { value: 'WEDNESDAY', text: 'Среда' },
      { value: 'THURSDAY', text: 'Четверг' },
      { value: 'FRIDAY', text: 'Пятница' },
      { value: 'SATURDAY', text: 'Суббота' },
      { value: 'SUNDAY', text: 'Воскресенье' }
    ],
    hourItems: Array.from({ length: 24 }, (v, i) => i)
  }),
  methods: {
    addRule () {
      this.value.pin_entrance_schedule.push({ enabled: false, pin: '', days: [], exceptions: [] })
      this.$emit('input', this.value)
    },
    removeRule (ri) {
      this.value.pin_entrance_schedule.splice(ri, 1)
      this.$emit('input', this.value)
    },
    addDay (ri) {
      this.value.pin_entrance_schedule[ri].days.push({ start_days: [], from: '', to: '' })
      this.$emit('input', this.value)
    },
    removeDay (ri, di) {
      this.value.pin_entrance_schedule[ri].days.splice(di, 1)
      this.$emit('input', this.value)
    },
    addException (ri) {
      this.value.pin_entrance_schedule[ri].exceptions.push({ date: '', from: '', to: '' })
      this.$emit('input', this.value)
    },
    removeException (ri, ei) {
      this.value.pin_entrance_schedule[ri].exceptions.splice(ei, 1)
      this.$emit('input', this.value)
    },
    togglePin (ri) {
      this.$set(this.pinVisible, ri, !this.pinVisible[ri])
    },
    toHint (row) {
      if (!TIME_RE.test(row.from || '') || !TIME_RE.test(row.to || '')) return ''
      if (row.to === row.from) return '24 часа'
      return row.to < row.from ? 'Окончание на следующий день' : ''
    },
    isIntInRange (v, min, max) {
      if (v === '' || v === null || v === undefined) return false
      const n = Number(v)
      return Number.isInteger(n) && n >= min && n <= max
    },
    validate () {
      const errors = {}
      const cfg = this.value.configuration
      for (const key of ['sync_interval_min', 'telemetry_interval_min', 'screensaver_interval']) {
        if (!this.isIntInRange(cfg[key], 1, 2147483647)) errors[`skud.configuration.${key}`] = ['Целое число не меньше 1']
      }
      if (!this.isIntInRange(cfg.volume, 0, 100)) errors['skud.configuration.volume'] = ['Целое число 0–100']
      this.value.pin_entrance_schedule.forEach((rule, ri) => {
        const base = `skud.pin_entrance_schedule[${ri}]`
        if (rule.pin) {
          if (!/^\d{4}$/.test(rule.pin)) errors[`${base}.pin`] = ['PIN должен содержать четыре цифры']
        } else if (rule.enabled) {
          errors[`${base}.pin`] = ['PIN должен содержать четыре цифры']
        }
        let completeRows = 0
        rule.days.forEach((day, di) => {
          let complete = true
          if (!day.start_days || !day.start_days.length) { errors[`${base}.days[${di}].start_days`] = ['Выберите хотя бы один день']; complete = false }
          if (!TIME_RE.test(day.from || '')) { errors[`${base}.days[${di}].from`] = ['Время в формате HH:mm']; complete = false }
          if (!TIME_RE.test(day.to || '')) { errors[`${base}.days[${di}].to`] = ['Время в формате HH:mm']; complete = false }
          if (complete) completeRows++
        })
        rule.exceptions.forEach((ex, ei) => {
          let complete = true
          if (!ex.date || !moment(ex.date, 'YYYY-MM-DD', true).isValid()) { errors[`${base}.exceptions[${ei}].date`] = ['Реальная дата']; complete = false }
          if (!TIME_RE.test(ex.from || '')) { errors[`${base}.exceptions[${ei}].from`] = ['Время в формате HH:mm']; complete = false }
          if (!TIME_RE.test(ex.to || '')) { errors[`${base}.exceptions[${ei}].to`] = ['Время в формате HH:mm']; complete = false }
          if (complete) completeRows++
        })
        if (rule.enabled && completeRows === 0) {
          errors[base] = [`Правило ${ri + 1}: добавьте хотя один заполненный период или исключение`]
        }
      })
      if (!this.isIntInRange(this.value.reboot_schedule.min, 0, 59)) errors['skud.reboot_schedule.min'] = ['Целое число 0–59']
      this.errors = errors
      if (Object.keys(errors).length) {
        this.revealPanels(Object.keys(errors))
        return false
      }
      return true
    },
    setServerErrors (errors) {
      const mapped = {}
      for (const e of errors || []) {
        if (!e || typeof e.path !== 'string') continue
        const m = e.path.match(/^skud\.pin_entrance_schedule\[(\d+)\]/)
        const prefix = m ? `Правило ${Number(m[1]) + 1}: ` : ''
        mapped[e.path] = [prefix + (e.message || '')]
      }
      this.errors = { ...this.errors, ...mapped }
      this.revealPanels(Object.keys(mapped))
    },
    revealPanels (paths) {
      const panels = new Set(this.panel)
      for (const p of paths) {
        if (p.includes('pin_entrance_schedule')) panels.add(1)
        else if (p.includes('reboot_schedule')) panels.add(2)
        else if (p.includes('configuration')) panels.add(0)
      }
      this.panel = [...panels]
    }
  }
}
</script>
